import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaSearch, FaCheckCircle, FaTimesCircle, FaClock, FaSpinner,
  FaArrowLeft, FaSync, FaReceipt, FaShieldAlt, FaInfoCircle,
  FaCalendarAlt, FaRupeeSign, FaBuilding, FaUser, FaCreditCard,
  FaChevronDown, FaChevronUp, FaBolt, FaLock, FaHeadset
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { getApiUrl } from '../config/api';
import './RefundStatusPage.css';

/* ── constants ─────────────────────────────────────────────── */
const STATUS_META = {
  requested:    { label: 'Refund Requested',  color: 'orange', step: 1 },
  approved:     { label: 'Refund Approved',   color: 'blue',   step: 2 },
  initiated:    { label: 'Refund Initiated',  color: 'blue',   step: 3 },
  processing:   { label: 'Bank Processing',   color: 'blue',   step: 4 },
  completed:    { label: 'Refund Completed',  color: 'green',  step: 5 },
  rejected:     { label: 'Refund Rejected',   color: 'red',    step: -1 },
  not_eligible: { label: 'No Refund Yet',      color: 'gray',   step: 0 },
  not_found:    { label: 'Not Found',         color: 'gray',   step: 0 },
};

const TIMELINE_STEPS = [
  { key: 'requested',  label: 'Requested' },
  { key: 'approved',   label: 'Approved' },
  { key: 'initiated',  label: 'Initiated' },
  { key: 'processing', label: 'Bank Processing' },
  { key: 'completed',  label: 'Completed' },
];

const EVENT_LABEL_MAP = {
  payment_captured:        { label: 'Payment Successful',          icon: '✅' },
  booking_cancelled:       { label: 'Booking Cancelled',           icon: '🚫' },
  refund_requested:        { label: 'Refund Requested',            icon: '📋' },
  refund_approved:         { label: 'Refund Approved',             icon: '✔️' },
  refund_initiated:        { label: 'Refund Initiated',            icon: '🔄' },
  webhook_refund_processed:{ label: 'Processed by Gateway',        icon: '⚡' },
  refund_completed:        { label: 'Refund Completed',            icon: '🎉' },
  refund_rejected:         { label: 'Refund Rejected',             icon: '❌' },
  webhook_payment_captured:{ label: 'Payment Confirmed',           icon: '✅' },
};

/* ── helper: format date ──────────────────────────────────── */
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtAmount = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

/* ══════════════════════════════════════════════════════════ */
const RefundStatusPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /* search form */
  const [searchType, setSearchType] = useState('bookingId');
  const [searchValue, setSearchValue] = useState(searchParams.get('bookingId') || '');
  const [email, setEmail] = useState('');

  /* state */
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refund, setRefund] = useState(null);
  const [error, setError] = useState(null);
  const [myRefunds, setMyRefunds] = useState([]);
  const [myRefundsLoading, setMyRefundsLoading] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [myBookingsLoading, setMyBookingsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showTimeline, setShowTimeline] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;

  /* auto-fill email from localStorage */
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      if (u.email) setEmail(u.email);
    } catch {}
  }, []);

  /* pre-fill from URL query params */
  useEffect(() => {
    const bid = searchParams.get('bookingId');
    const pid = searchParams.get('paymentId');
    const rid = searchParams.get('refundId');
    if (bid) { setSearchType('bookingId');  setSearchValue(bid); }
    else if (pid) { setSearchType('paymentId'); setSearchValue(pid); }
    else if (rid) { setSearchType('refundId');  setSearchValue(rid); }
  }, [searchParams]);

  /* fetch my-refunds for logged-in users */
  const loadMyRefunds = useCallback(async () => {
    if (!isLoggedIn) return;
    setMyRefundsLoading(true);
    try {
      const res = await fetch(getApiUrl('/api/refunds/my-refunds'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setMyRefunds(data.refunds || []);
    } catch {}
    finally { setMyRefundsLoading(false); }
  }, [isLoggedIn, token]);

  useEffect(() => { loadMyRefunds(); }, [loadMyRefunds]);

  const loadMyBookings = useCallback(async () => {
    if (!isLoggedIn) return;
    setMyBookingsLoading(true);
    try {
      const res = await fetch(getApiUrl('/api/refunds/my-bookings'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setMyBookings(data.bookings || []);
    } catch {}
    finally { setMyBookingsLoading(false); }
  }, [isLoggedIn, token]);

  useEffect(() => { loadMyBookings(); }, [loadMyBookings]);

  const runTrack = async (type, value, emailValue) => {
    if (!value.trim()) return;
    if (!emailValue.trim()) { setError('Please enter your registered email address.'); return; }
    setLoading(true);
    setError(null);
    setRefund(null);
    setSuggestions([]);

    const params = new URLSearchParams({ email: emailValue.trim() });
    params.set(type, value.trim());

    try {
      const res = await fetch(getApiUrl(`/api/refunds/track?${params.toString()}`));
      const data = await res.json();
      if (data.success && data.refund) {
        setRefund(data.refund);
        setError(null);
      } else {
        setError(data.message || 'No refund found.');
        if (data.suggestions?.length) setSuggestions(data.suggestions);
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBooking = (bookingId) => {
    setSearchType('bookingId');
    setSearchValue(bookingId);
    setError(null);
    setSuggestions([]);
    runTrack('bookingId', bookingId, email);
  };

  /* ── track refund ── */
  const handleTrack = async (e) => {
    e.preventDefault();
    runTrack(searchType, searchValue, email);
  };

  /* ── sync with razorpay ── */
  const handleSync = async () => {
    if (!refund?.paymentId || !isLoggedIn) return;
    setSyncing(true);
    try {
      const res = await fetch(getApiUrl(`/api/refunds/${refund.paymentId}/sync`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        // Reload the refund
        runTrack(searchType, searchValue, email);
      }
    } catch {}
    finally { setSyncing(false); }
  };

  /* ── select from my-refunds list ── */
  const handleSelectMyRefund = async (r) => {
    setLoading(true);
    setError(null);
    setRefund(null);
    try {
      const res = await fetch(getApiUrl(`/api/refunds/${r.paymentId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setRefund(data.refund);
      else setError(data.message);
    } catch {
      setError('Failed to load refund details.');
    } finally {
      setLoading(false);
    }
  };

  /* ── render ── */
  const statusMeta = refund ? (STATUS_META[refund.refundStatus] || STATUS_META.not_found) : null;

  return (
    <div className="rsp-page">
      <Navbar />
      <div className="rsp-container">

        {/* ── Hero header ── */}
        <div className="rsp-hero">
          <button className="rsp-back-btn" type="button" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>
          <div className="rsp-hero-body">
            <div className="rsp-hero-icon"><FaReceipt /></div>
            <div className="rsp-hero-text">
              <h1>Refund Status Tracker</h1>
              <p>Track your refund in real-time — no need to contact support</p>
              <div className="rsp-hero-badges">
                <span className="rsp-hero-badge"><FaBolt /> Real-time updates</span>
                <span className="rsp-hero-badge"><FaLock /> Secure verification</span>
                <span className="rsp-hero-badge"><FaHeadset /> Self-service</span>
              </div>
            </div>
          </div>
        </div>

        <div className="rsp-layout">

          {/* ══ LEFT: Search + My Refunds ══ */}
          <aside className="rsp-sidebar">

            {/* Search form */}
            <div className="rsp-card rsp-search-card">
              <h2 className="rsp-card-title"><FaSearch /> Track a Refund</h2>
              <p className="rsp-card-subtitle">Enter your booking details and registered email to view refund progress.</p>
              <form onSubmit={handleTrack} className="rsp-form">

                <div className="rsp-form-group">
                  <label>Search by</label>
                  <div className="rsp-type-tabs">
                    {[
                      { key: 'bookingId',  label: 'Booking ID' },
                      { key: 'paymentId',  label: 'Payment ID' },
                      { key: 'refundId',   label: 'Refund ID' },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        className={`rsp-type-tab${searchType === key ? ' active' : ''}`}
                        onClick={() => setSearchType(key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rsp-form-group">
                  <label htmlFor="rsp-sid">
                    {searchType === 'bookingId'  && 'Booking ID'}
                    {searchType === 'paymentId'  && 'Razorpay Payment ID'}
                    {searchType === 'refundId'   && 'Razorpay Refund ID'}
                  </label>
                  <input
                    id="rsp-sid"
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={
                      searchType === 'bookingId' ? 'Full ID or last 8 characters' :
                      searchType === 'paymentId' ? 'e.g. pay_…' :
                      'e.g. rfnd_…'
                    }
                    required
                  />
                </div>

                <div className="rsp-form-group">
                  <label htmlFor="rsp-email">
                    <FaShieldAlt className="rsp-shield-icon" /> Registered Email (for verification)
                  </label>
                  <input
                    id="rsp-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                  />
                </div>

                {error && (
                  <div className="rsp-error-msg">
                    <div className="rsp-error-row">
                      <FaTimesCircle /> <span>{error}</span>
                    </div>
                    {suggestions.length > 0 && (
                      <div className="rsp-suggestions">
                        <p className="rsp-suggestions-title">Your bookings — tap to track:</p>
                        <div className="rsp-suggestions-list">
                          {suggestions.map((s) => (
                            <button
                              key={s.bookingId}
                              type="button"
                              className="rsp-suggestion-item"
                              onClick={() => handleSelectBooking(s.bookingId)}
                            >
                              <span className="rsp-sug-name">{s.propertyName}</span>
                              <span className="rsp-sug-id">ID: …{s.shortId}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button type="submit" className="rsp-track-btn" disabled={loading}>
                  {loading ? <><FaSpinner className="rsp-spin" /> Tracking…</> : <><FaSearch /> Track Refund</>}
                </button>
              </form>
            </div>

            {/* Your Bookings picker (logged-in) */}
            {isLoggedIn && (
              <div className="rsp-card rsp-my-bookings-card">
                <h2 className="rsp-card-title"><FaBuilding /> Your Bookings</h2>
                <p className="rsp-card-subtitle">Tap a booking to auto-fill the correct ID — no typing needed.</p>
                {myBookingsLoading ? (
                  <div className="rsp-list-loading"><FaSpinner className="rsp-spin" /> Loading…</div>
                ) : myBookings.length === 0 ? (
                  <div className="rsp-list-empty">
                    <div className="rsp-list-empty-icon">📋</div>
                    No paid bookings found yet.
                  </div>
                ) : (
                  <div className="rsp-my-refunds-list">
                    {myBookings.map((b) => {
                      const m = STATUS_META[b.refundStatus] || STATUS_META.not_eligible;
                      return (
                        <button
                          key={b.bookingId}
                          type="button"
                          className="rsp-my-refund-item"
                          onClick={() => handleSelectBooking(b.bookingId)}
                        >
                          <div className="rsp-mri-name">{b.propertyName}</div>
                          <div className="rsp-mri-meta">
                            <span>{fmtAmount(b.totalAmount)}</span>
                            <span className={`rsp-status-chip rsp-status-${m.color}`}>{m.label}</span>
                          </div>
                          <div className="rsp-mri-date">
                            ID: <code className="rsp-id-code">{b.shortId}</code>
                            · {b.bookingStatus}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* My Refunds (logged-in only) */}
            {isLoggedIn && (
              <div className="rsp-card rsp-my-refunds-card">
                <h2 className="rsp-card-title"><FaReceipt /> My Refunds</h2>
                {myRefundsLoading ? (
                  <div className="rsp-list-loading"><FaSpinner className="rsp-spin" /> Loading…</div>
                ) : myRefunds.length === 0 ? (
                  <div className="rsp-list-empty">
                    <div className="rsp-list-empty-icon">📭</div>
                    No refunds found on your account.
                  </div>
                ) : (
                  <div className="rsp-my-refunds-list">
                    {myRefunds.map((r) => {
                      const m = STATUS_META[r.refundStatus] || STATUS_META.not_found;
                      return (
                        <button
                          key={r.paymentId}
                          className="rsp-my-refund-item"
                          onClick={() => handleSelectMyRefund(r)}
                        >
                          <div className="rsp-mri-name">{r.propertyName}</div>
                          <div className="rsp-mri-meta">
                            <span>{fmtAmount(r.refundAmount)}</span>
                            <span className={`rsp-status-chip rsp-status-${m.color}`}>{m.label}</span>
                          </div>
                          <div className="rsp-mri-date">{fmtDate(r.refundedAt || r.createdAt)}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </aside>

          {/* ══ RIGHT: Refund Detail ══ */}
          <main className="rsp-main">
            {!refund && !loading && (
              <div className="rsp-empty-state">
                <div className="rsp-empty-visual"><FaSearch /></div>
                <h2>Enter your refund details</h2>
                <p>Use your Booking ID, Payment ID, or Refund ID along with your email address to track the status of your refund.</p>
                <div className="rsp-help-tips">
                  <div className="rsp-help-tip">
                    <FaInfoCircle />
                    <span>Refunds usually take 5–7 business days to reflect in your bank account.</span>
                  </div>
                  <div className="rsp-help-tip">
                    <FaInfoCircle />
                    <span>Your Booking ID can be found in the booking confirmation email.</span>
                  </div>
                  <div className="rsp-help-tip">
                    <FaInfoCircle />
                    <span>Razorpay Payment IDs start with <code>pay_</code> and Refund IDs with <code>rfnd_</code>.</span>
                  </div>
                </div>
              </div>
            )}

            {loading && (
              <div className="rsp-loading-state">
                <div className="rsp-loading-ring" />
                <p>Fetching your refund status…</p>
              </div>
            )}

            {refund && !loading && (
              <div className="rsp-result">

                {/* ── Status Banner ── */}
                <div className={`rsp-status-banner rsp-banner-${statusMeta.color}`}>
                  <div className="rsp-banner-icon-wrap">
                    {statusMeta.color === 'green'  && <FaCheckCircle />}
                    {statusMeta.color === 'red'    && <FaTimesCircle />}
                    {statusMeta.color === 'orange' && <FaClock />}
                    {statusMeta.color === 'blue'   && <FaSpinner className="rsp-spin" />}
                    {statusMeta.color === 'gray'   && <FaInfoCircle />}
                  </div>
                  <div className="rsp-banner-text">
                    <div className="rsp-banner-status">{statusMeta.label}</div>
                    {refund.refundStatus === 'not_eligible' ? (
                      <div className="rsp-banner-amount">
                        {refund.statusReason || 'No refund process has started for this booking.'}
                      </div>
                    ) : (
                      <div className="rsp-banner-amount">
                        Refund of {fmtAmount(refund.refundAmount)}
                        {refund.currency && refund.currency !== 'INR' ? ` ${refund.currency}` : ''}
                        <span className="rsp-banner-percent">{refund.refundPercent || 100}% refund</span>
                      </div>
                    )}
                  </div>
                  {isLoggedIn && refund.refundStatus !== 'not_eligible' && refund.refundId && (
                    <button className="rsp-sync-btn" onClick={handleSync} disabled={syncing} title="Sync with Razorpay">
                      <FaSync className={syncing ? 'rsp-spin' : ''} />
                      {syncing ? 'Syncing…' : 'Sync'}
                    </button>
                  )}
                </div>

                {/* ── Quick stats ── */}
                <div className="rsp-quick-stats">
                  <div className="rsp-quick-stat">
                    <div className="rsp-quick-stat-label">Booking Amount</div>
                    <div className="rsp-quick-stat-value">{fmtAmount(refund.totalAmount)}</div>
                  </div>
                  <div className="rsp-quick-stat">
                    <div className="rsp-quick-stat-label">Refund Amount</div>
                    <div className={`rsp-quick-stat-value${refund.refundStatus !== 'not_eligible' ? ' green' : ''}`}>
                      {refund.refundStatus === 'not_eligible' ? '—' : fmtAmount(refund.refundAmount)}
                    </div>
                  </div>
                  <div className="rsp-quick-stat">
                    <div className="rsp-quick-stat-label">Property</div>
                    <div className="rsp-quick-stat-value blue" style={{ fontSize: '14px' }}>
                      {refund.propertyName || '—'}
                    </div>
                  </div>
                </div>

                {/* ── Not eligible info ── */}
                {refund.refundStatus === 'not_eligible' && (
                  <div className="rsp-card rsp-not-eligible-card">
                    <FaInfoCircle className="rsp-ne-icon" />
                    <div>
                      <div className="rsp-ne-title">Why is there no refund?</div>
                      <div className="rsp-ne-text">{refund.statusReason}</div>
                      <ul className="rsp-ne-list">
                        <li>Booking status: <strong>{refund.bookingStatus || '—'}</strong></li>
                        <li>Payment status: <strong>{refund.paymentStatus || '—'}</strong></li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* ── Progress Stepper ── */}
                {refund.refundStatus !== 'rejected' && refund.refundStatus !== 'not_eligible' && (
                  <div className="rsp-card rsp-stepper-card">
                    <p className="rsp-stepper-title">Refund Progress</p>
                    <div className="rsp-stepper">
                      {TIMELINE_STEPS.map((step, idx) => {
                        const currentStep = statusMeta.step;
                        const stepNum = idx + 1;
                        const isDone   = currentStep >= stepNum;
                        const isActive = currentStep === stepNum;
                        return (
                          <React.Fragment key={step.key}>
                            <div className={`rsp-step${isDone ? ' done' : ''}${isActive ? ' active' : ''}`}>
                              <div className="rsp-step-circle">
                                {isDone ? <FaCheckCircle /> : <span>{stepNum}</span>}
                              </div>
                              <div className="rsp-step-label">{step.label}</div>
                            </div>
                            {idx < TIMELINE_STEPS.length - 1 && (
                              <div className={`rsp-step-line${currentStep > stepNum ? ' done' : ''}`} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Rejected banner */}
                {refund.refundStatus === 'rejected' && (
                  <div className="rsp-card rsp-rejected-card">
                    <div className="rsp-rejected-icon"><FaTimesCircle /></div>
                    <div>
                      <div className="rsp-rejected-title">Refund Rejected</div>
                      <div className="rsp-rejected-reason">
                        {refund.notes || 'Please contact support for more information.'}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Info Grid ── */}
                <div className="rsp-card rsp-info-card">
                  <p className="rsp-info-card-header">Refund Details</p>
                  <div className="rsp-info-grid">
                    <div className="rsp-info-item">
                      <div className="rsp-info-icon-wrap"><FaBuilding className="rsp-info-icon" /></div>
                      <div>
                        <div className="rsp-info-label">Property</div>
                        <div className="rsp-info-value">{refund.propertyName || '—'}</div>
                      </div>
                    </div>
                    <div className="rsp-info-item">
                      <div className="rsp-info-icon-wrap"><FaUser className="rsp-info-icon" /></div>
                      <div>
                        <div className="rsp-info-label">Guest</div>
                        <div className="rsp-info-value">{refund.guestName || '—'}</div>
                      </div>
                    </div>
                    <div className="rsp-info-item">
                      <div className="rsp-info-icon-wrap"><FaRupeeSign className="rsp-info-icon" /></div>
                      <div>
                        <div className="rsp-info-label">Booking Amount</div>
                        <div className="rsp-info-value">{fmtAmount(refund.totalAmount)}</div>
                      </div>
                    </div>
                    <div className="rsp-info-item">
                      <div className="rsp-info-icon-wrap green"><FaRupeeSign className="rsp-info-icon" /></div>
                      <div>
                        <div className="rsp-info-label">Refund Amount</div>
                        <div className="rsp-info-value rsp-green">{fmtAmount(refund.refundAmount)}</div>
                      </div>
                    </div>
                    <div className="rsp-info-item">
                      <div className="rsp-info-icon-wrap"><FaCalendarAlt className="rsp-info-icon" /></div>
                      <div>
                        <div className="rsp-info-label">Request Date</div>
                        <div className="rsp-info-value">{fmtDate(refund.createdAt)}</div>
                      </div>
                    </div>
                    <div className="rsp-info-item">
                      <div className="rsp-info-icon-wrap green"><FaCalendarAlt className="rsp-info-icon" /></div>
                      <div>
                        <div className="rsp-info-label">Completion Date</div>
                        <div className="rsp-info-value">{fmtDate(refund.refundedAt)}</div>
                      </div>
                    </div>
                    <div className="rsp-info-item">
                      <div className="rsp-info-icon-wrap"><FaCreditCard className="rsp-info-icon" /></div>
                      <div>
                        <div className="rsp-info-label">Payment ID</div>
                        <div className="rsp-info-value rsp-mono">{refund.razorpayPaymentId || '—'}</div>
                      </div>
                    </div>
                    <div className="rsp-info-item">
                      <div className="rsp-info-icon-wrap"><FaShieldAlt className="rsp-info-icon" /></div>
                      <div>
                        <div className="rsp-info-label">Refund ID</div>
                        <div className="rsp-info-value rsp-mono">{refund.refundId || '—'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Timeline ── */}
                {refund.timeline?.length > 0 && (
                  <div className="rsp-card">
                    <button
                      className="rsp-section-toggle"
                      onClick={() => setShowTimeline((v) => !v)}
                    >
                      <span><FaCalendarAlt /> Refund Timeline</span>
                      {showTimeline ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    {showTimeline && (
                      <div className="rsp-timeline">
                        {refund.timeline.map((event, idx) => {
                          const meta = EVENT_LABEL_MAP[event.event] || { label: event.label || event.event, icon: '📌' };
                          const isLast = idx === refund.timeline.length - 1;
                          return (
                            <div key={idx} className={`rsp-tl-item${isLast ? ' last' : ''}`}>
                              <div className="rsp-tl-dot">{meta.icon}</div>
                              <div className="rsp-tl-body">
                                <div className="rsp-tl-label">{meta.label}</div>
                                <div className="rsp-tl-date">{fmtDateTime(event.ts)}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Additional Details (collapsible) ── */}
                <div className="rsp-card">
                  <button
                    className="rsp-section-toggle"
                    onClick={() => setShowDetails((v) => !v)}
                  >
                    <span><FaInfoCircle /> Additional Information</span>
                    {showDetails ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  {showDetails && (
                    <div className="rsp-detail-rows">
                      {[
                        { label: 'Booking ID',    value: String(refund.bookingId || '—') },
                        { label: 'Booking Type',  value: refund.bookingType || '—' },
                        { label: 'Refund %',      value: `${refund.refundPercent || 100}%` },
                        { label: 'Refund Method', value: 'Original Payment Method (Razorpay)' },
                        { label: 'Notes',         value: refund.notes || 'None' },
                      ].map(({ label, value }) => (
                        <div key={label} className="rsp-detail-row">
                          <span className="rsp-detail-label">{label}</span>
                          <span className="rsp-detail-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Help note ── */}
                <div className="rsp-help-note">
                  <FaInfoCircle />
                  <div>
                    <strong>Refund processing time:</strong> Once initiated, bank refunds typically take
                    5–7 business days. UPI refunds may arrive within 1–2 business days.
                    If you haven't received your refund after 10 days, please contact our support team.
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default RefundStatusPage;
